import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Button,
  Modal,
  Box,
  TextField,
  Stack,
  Alert,
  ButtonGroup,
  useTheme,
} from "@mui/material";
import {
  CreatePriceAlertApiArg,
  CurrencyConfigRes,
  useCreatePriceAlertMutation,
} from "src/store/features/api/generated";
import { trackEvent } from "src/Amplitude";
import { Link } from "react-router-dom";
import Preloader from "src/utils/Preloader";
import { CoinPerformanceStyles } from "src/components/coins/styles/CoinPerformance.styles";
import CloseIcon from "@mui/icons-material/Close";
import { togglePriceAlertModal, usePriceAlertState } from "src/store/features/PriceAlerts.slice";
import { useAppDispatch } from "src/store/store";
import { toggleFilterOptions } from "src/store/features/markets.slice";
import formatCoinName from "src/utils/formatCoinName";
import NumberFormatCustom from "src/components/trade/NumberField";
import usePermission from "src/utils/hooks/usePermission";
import { VerificationBanner } from "src/components/core/VerificationBanner";

interface PriceAlertsProps {
  price?: number;
  currencyConfig?: CurrencyConfigRes;
  subtitle?: string;
  logoUrl?: string;
  shouldRedirect: boolean;
}

const PriceAlertModel = (props: PriceAlertsProps) => {
  // ------------- REDUX---------------------
  const [postPriceAlert, { reset, isSuccess, isError, error }] = useCreatePriceAlertMutation();
  const { displayPriceAlert } = usePriceAlertState();
  const dispatchRedux = useAppDispatch();
  const inputRef = useRef<HTMLDivElement | null>(null);
  const { price = 0, currencyConfig, subtitle, logoUrl, shouldRedirect } = props;
  const classes = CoinPerformanceStyles();
  const theme = useTheme();
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    borderRadius: "10px",
    bgcolor: "background.paper",
    boxShadow: 24,
    [theme.breakpoints.down("md")]: {
      width: 350,
    },
  };

  // ------------- CONSTS---------------------
  const MODEL_TITLE = "Set Price Alert";
  // get current url slug for the "here" redirect

  const MODEL_SUBTITLE =
    "Receive a notification when the price crosses the target price. Read more about price alerts here";
  const INVALID_WARNING = "Price alert is set at the current market price. Please set a new price";
  const RANGE_ERROR = "Please set the price alert with a deviation greater than +/- 0.5%";
  const MESSAGE =
    "To add alerts to coins in this list, we will need you to verify your account. We will need your personal details, proof of ID & address and a selfie for this.";
  // -------------PRICE ALERT STATE----------------------
  const [invalidTarget, setInvalidTarget] = useState(false);
  const [priceAlertLoading, setPriceAlertLoading] = useState(false);
  const [formattedPrice, setFormattedPrice] = useState<number>(price);
  const [isRange, setIsRange] = useState(true);
  const [validTarget, setValidTarget] = useState("RED");

  // Make sure it is not withing +/- 0.5%
  const checkPriceLimit = (price: number, target: number) => {
    const lower = target * 0.995;
    const upper = target * 1.005;
    return price >= lower && price <= upper;
  };

  const targetPriceHandler = (event: any) => {
    formatPrice(+event.target.value);
  };

  const submitTargetOrder = (price: number, code: string) => {
    const reqBody: CreatePriceAlertApiArg = {
      priceAlertDto: { crossOverPrice: price, code },
    };
    setPriceAlertLoading(true);
    postPriceAlert(reqBody);

    if (isSuccess) {
      setPriceAlertLoading(false);
      trackEvent(`Submit_fulfilled`, { token: currencyConfig?.code }, "Price_Alert");
    }
    if (isError) {
      setPriceAlertLoading(false);
      trackEvent(`Submit_Price_Alert_rejected`, {
        token: currencyConfig?.code,
      });
    }
  };
  const formatPrice = (price: number) => {
    if (price < 1) {
      setFormattedPrice(price);
    } else if (price >= 1) {
      setFormattedPrice(Math.round(price * 100) / 100);
    } else {
      return setFormattedPrice(price);
    }
  };
  const percentageHandler = (value: number, price: number) => {
    const formattedPercentageNumber = price + price * value;
    formatPrice(formattedPercentageNumber);
  };

  const validatePriceAlert = (inputValue: number, currentPrice: number) => {
    const formatted = currentPrice > 1 ? Math.round(currentPrice * 100) / 100 : currentPrice;
    if (inputValue === formatted) {
      setValidTarget(theme.palette.error.main);
      setInvalidTarget(true);
    } else {
      setValidTarget(theme.palette.primary.main);
      setInvalidTarget(false);
    }
  };

  const { userPermission } = usePermission();

  // -------------EFFECTS----------------------

  const handleClose = () => {
    dispatchRedux(togglePriceAlertModal());
    setPriceAlertLoading(false);

    reset();
  };

  // SELECTS ALL INPUT TEXT
  const handleBoxClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    validatePriceAlert(formattedPrice, price);
    const isRange = checkPriceLimit(price, formattedPrice);

    setIsRange(isRange);
  }, [formattedPrice]);
  // set base price.
  useEffect(() => {
    formatPrice(price);
  }, [price]);

  return (
    <>
      {userPermission === "guest" && (
        <VerificationBanner open={displayPriceAlert} onClose={handleClose} message={MESSAGE} />
      )}
      {userPermission !== "guest" && (
        <Modal
          open={displayPriceAlert}
          onClose={handleClose}
          aria-labelledby="parent-modal-title"
          aria-describedby="parent-modal-description"
          sx={{ minHeight: "500px" }}>
          <Box sx={style}>
            <Stack spacing={2}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{
                  width: "100%",
                  borderTopLeftRadius: "10px",
                  borderTopRightRadius: "10px",
                  bgcolor: theme.palette.primary.main,
                  padding: "20px 25px 15px 25px",
                }}>
                <img src={logoUrl} alt={`${subtitle} logo`} className={classes.logoImage} />
                <Stack direction="column" justifyContent="center" alignItems="center">
                  <Typography id="cardTitle" sx={{ fontWeight: 900, color: "#fff" }}>
                    {` ${MODEL_TITLE}`}
                  </Typography>
                  <Typography id="priceModelTitle" style={{ fontWeight: 600, color: "#fff" }}>
                    {currencyConfig && formatCoinName(currencyConfig.name)}
                  </Typography>
                </Stack>
                <CloseIcon sx={{ cursor: "pointer", color: "#fff" }} onClick={handleClose} />
              </Stack>
              <Typography sx={{ padding: "10px 25px 0px 25px" }} id="explainer">
                {MODEL_SUBTITLE}
              </Typography>
            </Stack>
            {!isSuccess && !isError && (
              <Stack sx={{ mt: 2, padding: "5px 25px 15px 25px" }} spacing={2}>
                <TextField
                  onClick={handleBoxClick}
                  id="outlined-number"
                  label="Alert Price"
                  variant="outlined"
                  inputRef={inputRef}
                  onFocus={(event: any) => event.target.select()} // select all
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    inputProps: {
                      min: 0,
                      step: `${price > 1 ? "1" : "0.01"}`,
                      translate: "no",
                    },
                  }}
                  sx={{
                    input: {
                      color: validTarget,
                    },
                  }}
                  onChange={targetPriceHandler}
                  value={formattedPrice}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {priceAlertLoading && <Preloader />}
                {!priceAlertLoading && (
                  <>
                    <ButtonGroup
                      variant="outlined"
                      aria-label="outlined button group"
                      translate="no">
                      <Button disableRipple onClick={() => percentageHandler(-0.3, price)}>
                        -30%
                      </Button>
                      <Button disableRipple onClick={() => percentageHandler(-0.2, price)}>
                        -20%
                      </Button>
                      <Button disableRipple onClick={() => percentageHandler(-0.1, price)}>
                        -10%
                      </Button>
                      <Button disableRipple onClick={() => percentageHandler(0.1, price)}>
                        10%
                      </Button>
                      <Button disableRipple onClick={() => percentageHandler(0.2, price)}>
                        20%
                      </Button>
                      <Button disableRipple onClick={() => percentageHandler(0.3, price)}>
                        30%
                      </Button>
                    </ButtonGroup>
                    {isRange && !invalidTarget && <Alert severity="error">{RANGE_ERROR}</Alert>}
                    {invalidTarget && (
                      <Alert
                        sx={{
                          mt: 3,
                          mb: 3,
                          borderRadius: "10px",
                          backgroundColor: "#E8F6FF",
                          color: theme.palette.primary.main,
                        }}
                        severity="info">
                        {INVALID_WARNING}
                      </Alert>
                    )}
                    <Button
                      onClick={() => {
                        trackEvent(`Submit_Price_Alert`, {
                          Submit_Price_Alert: currencyConfig?.code,
                        });
                        if (currencyConfig) submitTargetOrder(formattedPrice, currencyConfig?.code);
                      }}
                      disabled={invalidTarget || isRange}
                      variant="contained"
                      sx={{ mt: 2 }}>
                      {invalidTarget ? "Disabled" : "Submit"}
                    </Button>
                  </>
                )}
              </Stack>
            )}

            {isSuccess && (
              <Alert
                sx={{
                  mt: 5,
                  cursor: "pointer",
                  margin: "10px 25px 15px 25px",
                }}
                severity="success">
                Price Alert Placed — check it out
                {shouldRedirect && (
                  <Link
                    style={{ marginLeft: "3px" }}
                    to={"/markets"}
                    onClick={() => {
                      dispatchRedux(toggleFilterOptions("Alerts"));
                      dispatchRedux(togglePriceAlertModal());
                    }}>
                    here
                  </Link>
                )}
                {/* {handle redirect if not on the coins page} */}
                {!shouldRedirect && (
                  <span
                    style={{
                      marginLeft: "3px",
                      textDecoration: "underline",
                    }}
                    onClick={() => {
                      dispatchRedux(toggleFilterOptions("Alerts"));
                      dispatchRedux(togglePriceAlertModal());
                    }}>
                    here
                  </span>
                )}
                !
              </Alert>
            )}
            {isError && (
              <Alert
                sx={{
                  mt: 5,
                  borderRadius: "10px",
                  margin: "10px 25px 15px 25px",
                }}
                severity="error">
                {error?.message}
              </Alert>
            )}
          </Box>
        </Modal>
      )}
    </>
  );
};

export default PriceAlertModel;
